import { Auth } from '../../auth/Auth';
import { SDK } from '../../index';
import { IDoryConfig } from '../../interface/IDoryConfig';
import StreamingAbstract from '../abstract/Streaming';

import { AlertStreaming } from './alert/AlertStreaming';
import { MarketDepthStreaming } from './marketDepth/MarketDepthStreaming';
import { NewsStreaming } from './news/NewsStreaming';
import { QuoteStreaming } from './quote/QuoteStreaming';

/**
 * This service offers streaming of RICs. The service creates a low latency, full duplex, cross-domain communication channel between the browser and the web server. Under the hood `SockJS` library tries to use native  * WebSockets first. If that fails it can use a variety of browser-specific transport protocols and presents them through WebSocket-like abstractions.
 *
 * Because of the full duplex communication, most Streaming methods do not support promises and instead rely on callbacks.
 *
 * ---
 *
 * ### How to use
 *
 * - `Streaming` only supports one active WebSocket connection, because of this:
 *   - `connect()` not create a new connection when:
 *     - another connect attempt is pending ⇒ return pending `promise`
 *     - already connected to WebSocket ⇒ return `true`
 *     - already reconnecting ⇒ throw Error (`Socket not available`)
 *   - `connect()` will register callbacks only on the first successful call
 * - `Streaming` registers a heartbeat function in order to monitor connection
 *   - can be configured when calling `connect(options, ...)` via `options.monitor`
 *   - when a heartbeat tick does not complete `Streaming` will:
 *     - close connection to WebSocket
 *     - call all callbacks passed to `addConnectionLostListener()`
 *     - start reconnecting (every 4 seconds)
 *     - ⚠️ while reconnecting new subscriptions are not registered & thus will not be resubscribed
 *   - when reconnect is successful `Streaming` will:
 *     - test the connection
 *     - restart the heartbeat
 *     - resubscribing all previous subscriptions
 *     - call all callbacks passed to `addConnectionRestoredListener()`
 *   - ⚠️ reconnecting does not connect to the old connection (which most likely was closed by Server), but starts a new one
 *   - ⚠️ resubscribing subscriptions does not connect to the old subscriptions, but starts new ones
 * - Use `addConnectionLostListener()` to:
 *   - update global application state
 *   - prevent new subscriptions
 * - Use `addConnectionRestoredListener()` to:
 *   - update global application state
 * - `Quote.subscribe()` and `News.subscribe()`
 *   - Individual subscriptions are not notified if the connection to the connected websocket is lost
 *   - Resubscribing a subscription creates a brand new subscription & will call `subscribe` and `snapshot` callbacks
 *   - The application using `Streaming` needs to handle `disconnected`/`reconnecting` state for subscriptions
 */
export class BaseStreaming extends StreamingAbstract {
  /**
   * @hidden
   */
  static install(instance: SDK) {
    instance['_streaming'] = new BaseStreaming(instance.config, instance.Auth);
  }

  //#region Endpoints
  /**
   * Set sockjs-server endpoint
   * @category Endpoint
   *
   * @param newEndpoint full url to new endpoint
   */
  public setSockEndpoint(newEndpoint: string): void {
    this.config.streaming.sock_url = newEndpoint;
  }
  /**
   * Get sockjs-server endpoint
   * @category Endpoint
   */
  public getSockEndpoint(): string {
    return this.config.streaming.sock_url;
  }

  /**
   * Set websocket-server endpoint
   * @category Endpoint
   *
   * @param newEndpoint full url to new endpoint
   */
  public setWebSocketEndpoint(newEndpoint: string): void {
    this.config.streaming.ws_url = newEndpoint;
  }
  /**
   * Get websocket-server endpoint
   * @category Endpoint
   */
  public getWebSocketEndpoint(): string {
    return this.config.streaming.ws_url;
  }
  //#endregion

  private alertStreaming: AlertStreaming;
  /**
   * @category Service
   */
  public get Alert(): AlertStreaming {
    return this.alertStreaming;
  }

  private marketDepthStreaming: MarketDepthStreaming;
  /**
   * @category Service
   */
  public get MarketDepth(): MarketDepthStreaming {
    return this.marketDepthStreaming;
  }

  private newsStreaming: NewsStreaming;
  /**
   * @category Service
   */
  public get News(): NewsStreaming {
    return this.newsStreaming;
  }

  private quoteStreaming: QuoteStreaming;
  /**
   * @category Service
   */
  public get Quote(): QuoteStreaming {
    return this.quoteStreaming;
  }

  /**
   * @hidden
   */
  public get Services() {
    return [this.Alert, this.MarketDepth, this.News, this.Quote];
  }

  public get isServiceAvailable(): {
    Alert: boolean;
    MarketDepth: boolean;
    News: boolean;
    Quote: boolean;
  } {
    return {
      Alert: this.connected ? this.Alert.isAvailable : false,
      MarketDepth: this.connected ? this.MarketDepth.isAvailable : false,
      News: this.connected ? this.News.isAvailable : false,
      Quote: this.connected ? this.Quote.isAvailable : false,
    };
  }
  //#endregion

  /**
   * @hidden
   */
  constructor(config: IDoryConfig, serviceAuth: Auth) {
    super(config, serviceAuth);

    this.alertStreaming = new AlertStreaming(this);
    this.marketDepthStreaming = new MarketDepthStreaming(this);
    this.newsStreaming = new NewsStreaming(this);
    this.quoteStreaming = new QuoteStreaming(this);
  }

  /**
   * Identifies if user has streaming permission
   *
   * @returns boolean
   */
  public isAllowedStreaming(): Promise<boolean> {
    return this.Auth.getToken().then((token) => {
      return token && token.prm.indexOf('a:streaming') !== -1 ? true : false;
    });
  }

  protected clearSubscriptions(): void {
    this.Quote.clearSubscriptions();
    this.MarketDepth.clearSubscriptions();
    this.News.clearSubscriptions();
    this.Alert.clearSubscriptions();
  }

  protected resubscribeSubscriptions(): void {
    this.Quote.resubscribe();
    this.MarketDepth.resubscribe();
    this.News.resubscribe();
    this.Alert.resubscribe();
  }
}
